import {
  CheckoutOrderSummaryOrderItemFields,
  core_apimessages_CheckoutOrder_SubtotalDiscount_Type,
  core_apimessages_ShippingMethod,
} from '../schemas/rql';
import gql from 'graphql-tag';
import React from 'react';
import CheckoutOrderSummaryOrderItemSubtotal, {
  CheckoutOrderSummaryOrderItemSubtotalFragment,
} from './checkout_order_summary_order_item_subtotal';
import CheckoutOrderSummaryOrderItemShippingTotal, {
  CheckoutOrderSummaryOrderItemShippingTotalFragment,
} from './checkout_order_summary_order_item_shipping_total';
import { I18N as Translate } from '@reverbdotcom/commons/src/components/translate';
import { RCAlertBox } from '@reverbdotcom/commons/src/rc/RCAlertBox';
import URLHelpers from './shared/url_helpers';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import CheckoutOrderSummaryImage, { CheckoutOrderSummaryImageFragment } from './checkout_order_summary_image';
import CheckoutOrderQuantitySelector, { CheckoutOrderQuantitySelectorFragment } from './checkout_order_quantity_selector';
import CheckoutOrderShippingSelector, { CheckoutOrderShippingSelectorFragment } from './checkout_order_shipping_selector';
import experiments from '@reverbdotcom/commons/src/experiments';
import { useExpEnabled } from '@reverbdotcom/commons/src/user_hooks';

type FragmentOrder = CheckoutOrderSummaryOrderItemFields.Orders;

interface IProps {
  order: FragmentOrder;
  onQuantitySelect(order: { listingId: string, quantity: number }): void;
  onShippingMethodSelect(order: { listingId: string, shippingMethodType: core_apimessages_ShippingMethod }): void;
  actionsDisabled?: boolean;
}

export default function CheckoutOrderSummaryOrderItem({ order, onQuantitySelect, onShippingMethodSelect, actionsDisabled }: IProps) {
  function isListingOnSale() {
    return order.subtotal.discounted &&
      order.subtotal.discount.type === core_apimessages_CheckoutOrder_SubtotalDiscount_Type.SALE;
  }

  const messageSellerURL = URLHelpers.newDashboardMessagesPath(order.listingId, order.listing.shop.userUuid);
  const singlePageCheckoutExpEnabled = useExpEnabled(experiments.SINGLE_PAGE_CHECKOUT);

  return (
    <div className="g-container">
      <div className="g-col-2">
        <CheckoutOrderSummaryImage listing={order.listing} />
      </div>
      {order.cannotBeShippedToAddress ? (
        <div data-shipping-warning className="g-col-10">
          <RCAlertBox type="warning">
            <Translate text="discovery.checkout.orderSummary.cannotBeShippedToAddress" />
            <p className="mb-0">
              <CoreLink to={messageSellerURL} className="button button--blue mt-1 icon-l-comment-o" rel="nofollow">
                <Translate text="discovery.checkout.orderSummary.messageSeller" />
              </CoreLink>
            </p>
          </RCAlertBox>
        </div>
      ) : (
        <>
          <div className="g-col-7">
            {isListingOnSale() &&
              <p data-discount-applied className="checkout-order-summary__item--discount-applied">
                {order.subtotal.discount.label}
              </p>
            }
            <p data-listing-title className="checkout-order-summary__item-title">{order.listing.title}</p>
            <p data-listing-sold-by className="checkout-order-summary__item-shop">
              <Translate
                text="discovery.checkout.orderSummary.soldBy"
                args={{ shopName: order.listing.shop.name }} />
            </p>
            <p data-listing-ships-from className="mb-0 size-90 opacity-60">
              <Translate
                text="discovery.checkout.orderSummary.shipsFrom"
                args={{ location: order.listing.shop.address.displayLocation }} />
            </p>
            <div className="d-flex">
              {singlePageCheckoutExpEnabled &&
                <CheckoutOrderShippingSelector
                  order={order}
                  onShippingMethodSelect={onShippingMethodSelect}
                  disabled={actionsDisabled}
                />
              }
              <CheckoutOrderQuantitySelector
                order={order}
                onQuantitySelect={onQuantitySelect}
                disabled={actionsDisabled}
              />
            </div>
          </div>
          <div className="g-col-3">
            <CheckoutOrderSummaryOrderItemSubtotal order={order} />
            <div className="mb-0 size-80 align-right">
              <CheckoutOrderSummaryOrderItemShippingTotal order={order} />
            </div>
            {order.taxIncluded &&
              <p data-order-tax-label className="mb-0 size-80 opacity-70 align-right">{order.taxLabel}</p>
            }
          </div>
        </>
      )}
    </div>
  );
}

export const CheckoutOrderSummaryOrderItemFragment = gql`
  fragment CheckoutOrderSummaryOrderItemFields on Checkout {
    _id
    orders {
      cannotBeShippedToAddress
      subtotal {
        discounted
        discount {
          label
          type
        }
      }
      taxIncluded
      taxLabel
      listingId
      listing {
        _id
        title
        shop {
          _id
          name
          userUuid
          address {
            displayLocation
          }
        }
      }
    }
    ...CheckoutOrderSummaryOrderItemSubtotalFields
    ...CheckoutOrderSummaryOrderItemShippingTotalFields
    ...CheckoutOrderSummaryImageFields
    ...CheckoutOrderQuantitySelectorFields
    ...CheckoutOrderShippingSelectorFields
  }
  ${CheckoutOrderSummaryOrderItemSubtotalFragment}
  ${CheckoutOrderSummaryOrderItemShippingTotalFragment}
  ${CheckoutOrderSummaryImageFragment}
  ${CheckoutOrderQuantitySelectorFragment}
  ${CheckoutOrderShippingSelectorFragment}
`;
